// ─────────────────────────────────────────────────────────────────────────────
// Push notification bridge for the iOS Capacitor build.
//
// This is a thin wrapper around @capacitor/push-notifications that:
//   1. No-ops on the web (Capacitor.isNativePlatform() === false).
//   2. Requests permission when the user explicitly asks for it (e.g. inside
//      Settings → "Enable notifications").
//   3. Registers the device with APNs and POSTs the resulting token to our
//      backend so we can target this user from the admin / cron jobs.
//
// The backend endpoint `POST /api/push/register` accepts:
//   { token: string, platform: "ios"|"android" }
// and persists it on the user document.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isNative = () => {
  try {
    // eslint-disable-next-line global-require
    const { Capacitor } = require("@capacitor/core");
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

const platformName = () => {
  try {
    // eslint-disable-next-line global-require
    const { Capacitor } = require("@capacitor/core");
    return Capacitor?.getPlatform?.() || "web";
  } catch {
    return "web";
  }
};

let listenersAttached = false;

/**
 * Ask the OS for notification permission and register the device with APNs.
 * Returns a status object so the caller can update its UI accordingly.
 */
export async function enablePushNotifications() {
  if (!isNative()) return { granted: false, reason: "not_native" };

  // eslint-disable-next-line global-require
  const { PushNotifications } = require("@capacitor/push-notifications");

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") {
    return { granted: false, reason: perm.receive };
  }

  if (!listenersAttached) {
    listenersAttached = true;

    PushNotifications.addListener("registration", async (token) => {
      try {
        await axios.post(
          `${API}/push/register`,
          { token: token.value, platform: platformName() },
          { withCredentials: true }
        );
      } catch {
        // Silent — the user's session might not be hot yet.
      }
    });

    PushNotifications.addListener("registrationError", () => {
      // No-op; we surface this via the return value of the initial register call.
    });
  }

  await PushNotifications.register();
  return { granted: true };
}

/** Read the current OS-level permission state. */
export async function checkPushPermission() {
  if (!isNative()) return { receive: "unsupported" };
  // eslint-disable-next-line global-require
  const { PushNotifications } = require("@capacitor/push-notifications");
  return PushNotifications.checkPermissions();
}
